import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";

import { Image, Pagination, Empty } from "antd";
import { Col, Row, Container } from "react-bootstrap";

import BreadcrumbLayout from "../../../component/Breadcrumb";
import { useTranslation } from "react-i18next";

import { ImageGalleryService } from "../../../Services/ImageGalleryService";

import demoUser from "../../../assets/images/broken-img.jpg";
import loadingImg from "../../../assets/images/loading-img.gif";

import ATCSidebar from "../ATCSidebar";
import Animation from "../../../assets/images/Animation.gif";


export default function MapGallery() {

  const { t, i18n } = useTranslation();

  const menuData =
    t(
      "menu.activities-of-council.cg-space-applications-centre",
      {
        returnObjects: true,
      }
    ) || {};

  const imageGalleryService = useMemo(
    () => new ImageGalleryService(),
    []
  );

  const [loadingTblData, setLoadingTblData] =
    useState(false);

  const [tblData, setTblData] = useState([]);

  const [lang, setLang] = useState(
    localStorage.getItem("language") === "en"
      ? 2
      : 1
  );

  const [currentPage, setCurrentPage] =
    useState(1);

  const [itemsPerPage, setItemsPerPage] =
    useState(9);


  /*
   * =====================================================
   * LANGUAGE CHANGE
   * =====================================================
   */

  useEffect(() => {

    setLang(
      localStorage.getItem("language") === "en"
        ? 2
        : 1
    );

  }, [i18n.language]);


  /*
   * =====================================================
   * GET GALLERY METADATA
   *
   * यहां केवल gallery की information आएगी।
   *
   * Images यहां download नहीं होंगी।
   * =====================================================
   */

  useEffect(() => {

    let isMounted = true;

    const getGalleryData = async () => {

      try {

        setLoadingTblData(true);

        /*
         * galleryTypeId = 3
         * language = lang
         * imageCategoryType = -1
         */

        const res =
          await imageGalleryService.GetImageListPublic(
            3,
            lang,
            -1
          );

        if (!isMounted) {
          return;
        }

        const tableData =
          res?.data?.table || [];

        /*
         * केवल metadata तैयार कर रहे हैं।
         */

        const initialData =
          tableData.map((item, index) => ({

            key: index + 1,

            imageId: item.imageId,

            description: item.description,

            imagenotes: item.imagenotes,

            doc: {
              docMimeType:
                item.docMimeType,

              docUniqueId:
                item.docUniqueId,
            },

            /*
             * शुरू में actual image नहीं है।
             */

            ImgUrl: null,

            /*
             * Image अभी load नहीं हुई।
             */

            imageLoaded: false,

            /*
             * Image loading में नहीं है।
             */

            imageLoading: false,

          }));

        setTblData(initialData);

        /*
         * नई language में हमेशा page 1
         */

        setCurrentPage(1);

      } catch (error) {

        console.error(
          "Error loading Map Gallery:",
          error
        );

        if (isMounted) {
          setTblData([]);
        }

      } finally {

        if (isMounted) {
          setLoadingTblData(false);
        }

      }
    };


    getGalleryData();


    return () => {
      isMounted = false;
    };

  }, [
    imageGalleryService,
    lang,
  ]);


  /*
   * =====================================================
   * CURRENT PAGE DATA
   * =====================================================
   */

  const indexOfLastItem =
    currentPage * itemsPerPage;

  const indexOfFirstItem =
    indexOfLastItem - itemsPerPage;

  const currentImages =
    tblData.slice(
      indexOfFirstItem,
      indexOfLastItem
    );


  /*
   * =====================================================
   * LOAD ONE IMAGE
   *
   * एक समय में जिस image की जरूरत है
   * उसी की API call होगी।
   * =====================================================
   */

  const loadImage = useCallback(
    async (itemIndex, item) => {

      /*
       * Image पहले से load हो चुकी है
       * तो दोबारा API call नहीं करेंगे।
       */

      if (
        item.imageLoaded ||
        item.imageLoading
      ) {
        return;
      }


      /*
       * अगर image ID नहीं है
       */

      if (
        item.doc?.docUniqueId === null ||
        item.doc?.docUniqueId === undefined
      ) {

        setTblData((prevData) => {

          const updatedData = [
            ...prevData,
          ];

          if (!updatedData[itemIndex]) {
            return prevData;
          }

          updatedData[itemIndex] = {
            ...updatedData[itemIndex],

            ImgUrl: demoUser,

            imageLoading: false,

            imageLoaded: true,
          };

          return updatedData;

        });

        return;
      }


      /*
       * =================================================
       * IMAGE LOADING START
       * =================================================
       */

      setTblData((prevData) => {

        const updatedData = [
          ...prevData,
        ];

        if (!updatedData[itemIndex]) {
          return prevData;
        }

        updatedData[itemIndex] = {
          ...updatedData[itemIndex],

          ImgUrl: loadingImg,

          imageLoading: true,
        };

        return updatedData;

      });


      try {

        /*
         * केवल इस image की API call
         */

        const res =
          await imageGalleryService.ViewDocument(
            item.doc.docUniqueId
          );


        if (res.status === 200) {

          const fileBlob =
            new Blob(
              [res.data],
              {
                type:
                  item.doc.docMimeType,
              }
            );


          const dataUrl =
            URL.createObjectURL(
              fileBlob
            );


          /*
           * Actual image URL set करें
           */

          setTblData((prevData) => {

            const updatedData = [
              ...prevData,
            ];

            if (!updatedData[itemIndex]) {
              return prevData;
            }

            updatedData[itemIndex] = {

              ...updatedData[itemIndex],

              ImgUrl: dataUrl,

              imageLoading: false,

              imageLoaded: true,

            };

            return updatedData;

          });

        } else {

          throw new Error(
            "Image API failed"
          );

        }

      } catch (error) {

        console.error(
          "Error fetching map image:",
          error
        );


        /*
         * अगर image download नहीं हुई
         * तो broken image दिखाएँगे।
         */

        setTblData((prevData) => {

          const updatedData = [
            ...prevData,
          ];

          if (!updatedData[itemIndex]) {
            return prevData;
          }

          updatedData[itemIndex] = {

            ...updatedData[itemIndex],

            ImgUrl: demoUser,

            imageLoading: false,

            imageLoaded: true,

          };

          return updatedData;

        });

      }

    },
    [imageGalleryService]
  );


  /*
   * =====================================================
   * CURRENT PAGE की IMAGES LOAD करें
   *
   * यही सबसे महत्वपूर्ण बदलाव है।
   * =====================================================
   */

  useEffect(() => {

    if (
      currentImages.length === 0
    ) {
      return;
    }


    currentImages.forEach(
      (item, index) => {

        /*
         * currentImages का index
         * tblData का actual index नहीं है।
         *
         * इसलिए first index जोड़ना जरूरी है।
         */

        const actualIndex =
          indexOfFirstItem + index;


        loadImage(
          actualIndex,
          item
        );

      }
    );

  }, [
    currentPage,
    itemsPerPage,
    tblData.length,
    indexOfFirstItem,
    loadImage,
  ]);


  /*
   * =====================================================
   * PAGINATION CHANGE
   * =====================================================
   */

  const handlePaginationChange = (
    page,
    pageSize
  ) => {

    /*
     * अगर page size बदला गया है
     * तो page 1 पर जाएँगे।
     */

    if (
      pageSize !== itemsPerPage
    ) {

      setCurrentPage(1);

    } else {

      setCurrentPage(page);

    }

    setItemsPerPage(pageSize);

  };


  /*
   * =====================================================
   * UI
   * =====================================================
   */

  return (
    <>
      <BreadcrumbLayout
        title="Map Gallery"
      />

      <Container fluid>

        <Row>

          {/* ==============================
              SIDEBAR
          =============================== */}

          <ATCSidebar
            menuData={menuData}
            type="CGSpaceApplicationsCentre"
          />


          {/* ==============================
              CONTENT
          =============================== */}

          <Col
            md={9}
            className="content order-md-last order-first"
          >

            <Row>

              {/* Heading */}

              <Col
                xs={12}
                className="heading"
              >

                <h3>
                  {menuData["map-gallery"]}
                </h3>

              </Col>


              <Col
                xs={12}
                className="mb-1 mt-1"
              >

                {/* Main API loading */}

                {loadingTblData && (
                  <img
                    src={Animation}
                    alt="Loading"
                  />
                )}


                {/* ============================
                    GALLERY
                ============================= */}

                {currentImages.length > 0 ? (

                  <>

                    <Row className="galleryWrap">

                      <Image.PreviewGroup>

                        {currentImages.map(
                          (slide) => (

                            <Col
                              lg={4}
                              key={slide.key}
                            >

                              <Image
                                src={
                                  slide.ImgUrl ||
                                  loadingImg
                                }
                                alt={
                                  "Image-" +
                                  slide.imageId
                                }
                              />

                            </Col>

                          )
                        )}

                      </Image.PreviewGroup>

                    </Row>


                    {/* ============================
                        PAGINATION
                    ============================= */}

                    <Row justify="center">

                      <Col>

                        <div
                          className="img-pagination"
                          style={{
                            paddingBottom:
                              "20px",
                          }}
                        >

                          <Pagination
                            current={
                              currentPage
                            }

                            pageSize={
                              itemsPerPage
                            }

                            total={
                              tblData.length
                            }

                            onChange={
                              handlePaginationChange
                            }

                            showSizeChanger={
                              true
                            }

                            pageSizeOptions={[
                              "9",
                              "18",
                              "36",
                              "72",
                            ]}
                          />

                        </div>

                      </Col>

                    </Row>

                  </>

                ) : (

                  /* ============================
                     EMPTY
                  ============================= */

                  <Row>

                    <Col>

                      {!loadingTblData && (

                        <Empty
                          description={
                            lang === 1 ? (

                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                वर्तमान में, प्रदर्शित करने
                                के लिए कोई डेटा नहीं है ।
                              </span>

                            ) : (

                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                Currently, There are no
                                data to display.
                              </span>

                            )
                          }
                        />

                      )}

                    </Col>

                  </Row>

                )}

              </Col>

            </Row>

          </Col>

        </Row>

      </Container>
    </>
  );
}